import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateObligationAssessmentResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateObligationAssessmentResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateObligationAssessmentResultInput {
  Id: string;
  /**
   * Optimistic-concurrency token (COI-622) — the result row's last-seen
   * `ModifiedAtTimestamp`. The legacy mutation's `where` is `Id` only, so it is
   * stripped on the Apollo leg and only the tRPC path locks on it.
   */
  OriginalTimestamp?: string | null;
  Rating?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
}

export const useUpdateObligationAssessmentResult = createMutationHook({
  name: 'updateObligationAssessmentResult',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.obligationAssessmentResult.update.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: UpdateObligationAssessmentResultInput) => input,
  // Invalidation parity with the legacy Apollo evictFields:
  // obligation_assessment_result (+ aggregate) and assessment.
  invalidate: (trpc) => [
    trpc.frontend.obligationAssessmentResult.pathKey(),
    trpc.frontend.assessment.pathKey(),
    // COI-889: the Obligations register's rating columns read the nested
    // `assessmentResults`.
    trpc.frontend.obligation.register.queryKey(),
  ],
  // The tRPC update either succeeds on exactly one row or throws, so a
  // successful response always maps to affected_rows: 1.
  mapResponse: (): UpdateObligationAssessmentResultMutation => ({
    update_obligation_assessment_result: {
      __typename: 'obligation_assessment_result_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateObligationAssessmentResultDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...input
  }: UpdateObligationAssessmentResultInput) => ({
    ...input,
  }),
  evictFields: [
    'obligation_assessment_result',
    'assessment',
    'obligation_assessment_result_aggregate',
  ],
});
